import moment from "moment";
import { parseLoginActivityMoment } from "./loginActivityTime";

export const API_DATETIME_EMPTY = "—";

export const MOMENT_FMT_EN_IN_CREATED_ON = "D MMM YYYY, h:mm A";

export const MOMENT_FMT_EN_IN_TABLE_DATETIME = "D MMM YYYY, HH:mm";

export const MOMENT_FMT_EN_IN_DATE_ONLY = "D MMM YYYY";

export const MOMENT_FMT_EN_US_MEDIUM_SHORT = "MMM D, YYYY, h:mm A";

export const MOMENT_FMT_DD_MMM_YYYY = "DD-MMM-YYYY";

export const MOMENT_FMT_DD_MMM_YYYY_HH_MM_A = "DD-MMM-YYYY hh:mm A";

export const MOMENT_FMT_DD_SLASH_MMM_YYYY_HH_MM_A = "DD/MMM/YYYY hh:mm A";

export const MOMENT_FMT_DD_MMM_YYYY_H_MM_A = "DD-MMM-YYYY h:mm A";

export const MOMENT_FMT_MMM_DD_YYYY = "MMM DD, YYYY";

export const MOMENT_FMT_MMM_DD_YYYY_H_MM_A = "MMM DD, YYYY, h:mm A";

export const MOMENT_FMT_MMM_D_YYYY_H_MM_A = "MMM D, YYYY, h:mm A";

export const MOMENT_FMT_MMMM_D_YYYY = "MMMM D, YYYY";

export const MOMENT_FMT_MMMM_D_YYYY_H_MM_A = "MMMM D, YYYY, h:mm A";

export const DEFAULT_API_DATETIME_LOCAL_MOMENT_FORMAT = "DD MMM YYYY, h:mm A";

const DD_MM_YYYY_FORMATS = [
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY H:mm",
  "D/M/YYYY HH:mm:ss",
  "D/M/YYYY HH:mm",
  "DD/MM/YYYY",
  "D/M/YYYY",
] as const;

const DD_MMM_YYYY_FORMATS = [
  "DD-MMM-YYYY hh:mm:ss A",
  "DD-MMM-YYYY hh:mm:ss",
  "DD-MMM-YYYY hh:mm A",
  "DD-MMM-YYYY h:mm:ss A",
  "DD-MMM-YYYY h:mm A",
  "DD-MMM-YYYY",
  "D-MMM-YYYY hh:mm A",
  "D-MMM-YYYY h:mm A",
  "D-MMM-YYYY",
] as const;

const FALLBACK_DATE_FORMATS = [
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD",
] as const;

type FormatApiDateTimeOptions = {
  emptyValue?: string;
  invalidValue?: string;
};

export type ParseApiDateTimeOptions = {
  assumeUtc?: boolean;
};

function normalizeMomentLocale(locale: string): string {
  return locale.trim().replace(/_/g, "-").toLowerCase();
}

function isEmptyApiDateInput(input: string | number | null | undefined): boolean {
  if (input === null || input === undefined) return true;
  return typeof input === "string" && !input.trim();
}

function parseEpochToLocalMoment(value: number): moment.Moment | null {
  const ms = Math.abs(value) < 1e12 ? value * 1000 : value;
  const parsed = moment.utc(ms).local();
  return parsed.isValid() ? parsed : null;
}

function normalizeIsoSpaceToT(trimmed: string): string {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(trimmed) && !trimmed.includes("T")) {
    return trimmed.replace(
      /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?|\d{2}:\d{2})/,
      "$1T$2"
    );
  }
  return trimmed;
}

function hasExplicitTimezoneOffset(value: string): boolean {
  return /Z$/i.test(value) || /[+-]\d{2}:\d{2}(:\d{2})?$/.test(value) || /[+-]\d{4}$/.test(value);
}

function formatParsedApiDateTime(parsed: moment.Moment, format: string, locale?: string): string {
  const local = parsed.local();
  if (locale) {
    return local.clone().locale(normalizeMomentLocale(locale)).format(format);
  }
  return local.format(format);
}

function formatApiDateTimeCore(
  input: string | number | null | undefined,
  format: string,
  options: FormatApiDateTimeOptions & { locale?: string; assumeUtc?: boolean } = {}
): string {
  const { locale, emptyValue = API_DATETIME_EMPTY, invalidValue, assumeUtc } = options;

  if (isEmptyApiDateInput(input)) return emptyValue;

  const parsed = assumeUtc
    ? parseApiDateTimeMoment(input, { assumeUtc: true })
    : (parseLoginActivityMoment(input) ?? parseApiDateTimeMoment(input));
  if (!parsed?.isValid()) {
    if (invalidValue !== undefined) return invalidValue;
    return typeof input === "string" && input.trim() ? input.trim() : emptyValue;
  }

  return formatParsedApiDateTime(parsed, format, locale);
}

function parseTimezonelessApiDateTime(
  trimmed: string,
  isoSpaceToT: string,
  assumeUtc: boolean
): moment.Moment {
  const zone = assumeUtc ? moment.utc : moment;

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoSpaceToT)) {
    let parsed = zone(isoSpaceToT, moment.ISO_8601, true);
    if (!parsed.isValid()) parsed = zone(isoSpaceToT);
    return parsed;
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}(\s+\d{1,2}:\d{2}(:\d{2})?)?$/.test(trimmed)) {
    let parsed = zone(trimmed, [...DD_MM_YYYY_FORMATS], true);
    if (!parsed.isValid()) {
      parsed = zone(trimmed, ["DD/MM/YYYY HH:mm", "DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY"]);
    }
    return parsed;
  }
  if (/^\d{1,2}-[A-Za-z]{3}-\d{4}/.test(trimmed)) {
    let parsed = zone(trimmed, [...DD_MMM_YYYY_FORMATS], true);
    if (!parsed.isValid()) parsed = zone(trimmed);
    return parsed;
  }
  let parsed = zone(trimmed, [...FALLBACK_DATE_FORMATS], true);
  if (!parsed.isValid()) parsed = zone(trimmed);
  return parsed;
}

export function parseApiDateTimeMoment(
  input: string | number | null | undefined,
  options: ParseApiDateTimeOptions = {}
): moment.Moment | null {
  const { assumeUtc = false } = options;

  if (input === null || input === undefined) return null;
  if (typeof input === "number" && !Number.isNaN(input)) {
    return parseEpochToLocalMoment(input);
  }

  const trimmed = String(input).trim();
  if (!trimmed) return null;

  const intStr = trimmed.replace(/\s/g, "");
  const num = Number(intStr);
  if (!Number.isNaN(num) && /^-?\d+$/.test(intStr)) {
    return parseEpochToLocalMoment(num);
  }

  const isoSpaceToT = normalizeIsoSpaceToT(trimmed);
  let parsed: moment.Moment;

  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    parsed = assumeUtc
      ? moment.utc(trimmed, "DD-MM-YYYY", true)
      : moment(trimmed, "DD-MM-YYYY", true);
  } else if (hasExplicitTimezoneOffset(isoSpaceToT)) {
    parsed = moment.parseZone(isoSpaceToT);
  } else {
    parsed = parseTimezonelessApiDateTime(trimmed, isoSpaceToT, assumeUtc);
  }

  if (!parsed.isValid()) return null;
  return assumeUtc ? parsed.local() : parsed;
}

export function formatApiDateTimeMomentLocal(
  input: string | number | null | undefined,
  format: string = DEFAULT_API_DATETIME_LOCAL_MOMENT_FORMAT,
  options?: FormatApiDateTimeOptions
): string {
  return formatApiDateTimeCore(input, format, options);
}

export function parseChatApiDateTimeMoment(
  input: string | number | null | undefined
): moment.Moment | null {
  return parseApiDateTimeMoment(input, { assumeUtc: true });
}

export function formatChatApiDateTimeMomentLocal(
  input: string | number | null | undefined,
  format: string = MOMENT_FMT_DD_MMM_YYYY_HH_MM_A,
  options?: FormatApiDateTimeOptions
): string {
  return formatApiDateTimeCore(input, format, { ...options, assumeUtc: true });
}

export function formatApiDateTimeLocal(
  input: string | number | null | undefined,
  locale: string = "en-in",
  momentFormat: string = MOMENT_FMT_EN_IN_TABLE_DATETIME,
  options?: FormatApiDateTimeOptions
): string {
  return formatApiDateTimeCore(input, momentFormat, { ...options, locale });
}

export function formatYmdLocalDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function ddMmYyyyToYmd(value: string): string | null {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  return `${yyyy}-${mm}-${dd}`;
}
